<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="true"
          :title="pageTitle"
          @onClickCloseButton="goBack"
      />
    </div>

    <div v-if="errorMessage" class="px-2 mt-2">
      <div class="alert alert-danger mb-0">
        <div class="alert-body p-1">
          <div class="fw-bold mb-0">{{ errorMessage }}</div>
          <ul v-if="hasInvalidClasses" class="mb-0 mt-1 ps-2">
            <li v-for="item in invalidClasses" :key="item.product_id" class="mb-25">
              <span class="fw-bold">{{ item.product_name }}</span>
              <span v-if="!item.expense_account_head_set"> &mdash; Depreciation Expense Account not set</span>
              <span v-if="!item.expense_account_head_set && !item.accumulated_account_head_set">,</span>
              <span v-if="!item.accumulated_account_head_set"> &mdash; Accumulated Depreciation Account not set</span>
            </li>
          </ul>
          <div v-if="hasInvalidClasses" class="mt-1 small">
            Set both account heads on these products from Product &amp; Service, then reopen this page.
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasPreview" class="px-2 mt-2">
      <div class="row gy-1">
        <div class="col-12 col-sm-6 col-lg-4">
          <div class="summary-tile h-100">
            <div class="summary-label">Depreciation Period</div>
            <div class="summary-value">{{ periodLabel }}</div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-lg-4">
          <div class="summary-tile summary-tile-accent h-100">
            <div class="summary-label">Total Depreciation</div>
            <div class="summary-value summary-amount">{{ formatAmount(preview.amount) }}</div>
          </div>
        </div>
        <div v-if="!isBalanced" class="col-12 col-lg-4">
          <div class="summary-tile summary-tile-danger h-100">
            <div class="summary-label">Balance Check</div>
            <div class="summary-value"><span class="badge bg-danger">Out of balance</span></div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isPosted" class="px-2 mt-2">
      <div class="posted-bar d-flex flex-wrap align-items-center gap-2">
        <span class="badge bg-success">Journal Posted</span>
        <span class="posted-item"><span class="posted-label">Voucher No</span> {{ preview.voucher_no }}</span>
        <span class="posted-item"><span class="posted-label">Date</span> {{ preview.format_voucher_date }}</span>
        <span class="posted-item"><span class="posted-label">Status</span> {{ preview.voucher_status }}</span>
        <span v-if="preview.note" class="posted-item"><span class="posted-label">Note</span> {{ preview.note }}</span>
      </div>
    </div>

    <div v-if="canCreate" class="px-2 mt-2">
      <div class="row gy-1 align-items-end">
        <div class="col-12 col-sm-6 col-lg-3">
          <label class="form-label fw-bold">Voucher Date <span class="text-danger">*</span></label>
          <input class="form-control" type="date" v-model="formData.date">
        </div>
        <div class="col-12 col-sm-6 col-lg-6">
          <label class="form-label fw-bold">Note</label>
          <input class="form-control" type="text" v-model="formData.note" placeholder="Optional narration for this voucher">
        </div>
        <div class="col-12 col-lg-3">
          <button
              :disabled="loading"
              class="btn btn-primary w-100"
              @click="submitJournal"
          >
            Create JV
          </button>
        </div>
      </div>
    </div>

    <ul v-if="hasPreview" class="nav nav-tabs px-2 pt-2 mt-2" role="tablist">
      <li class="nav-item">
        <a
            :class="['nav-link', { active: isLedgerTab }]"
            style="cursor: pointer"
            @click="activeTab = 'ledger'"
        >
          Ledger Preview
          <span class="badge bg-light-secondary ms-25">{{ lineCount }}</span>
        </a>
      </li>
      <li class="nav-item">
        <a
            :class="['nav-link', { active: isClassTab }]"
            style="cursor: pointer"
            @click="activeTab = 'class'"
        >
          Asset Class Breakdown
          <span class="badge bg-light-secondary ms-25">{{ classCount }}</span>
        </a>
      </li>
    </ul>

    <div v-if="hasPreview && isLedgerTab" class="px-2 mt-1 pb-2">
      <div class="table-responsive">
        <table class="table table-bordered table-hover mb-0">
          <thead>
          <tr>
            <th>Account Head</th>
            <th>Cost Centre</th>
            <th>Business Unit</th>
            <th>Project</th>
            <th class="text-end nowrap">Debit</th>
            <th class="text-end nowrap">Credit</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(line, index) in preview.lines" :key="index">
            <td class="fw-bold">{{ line.account_head_name }}</td>
            <td>{{ dimension(line.cost_centre_name) }}</td>
            <td>{{ dimension(line.business_name) }}</td>
            <td>{{ dimension(line.project_name) }}</td>
            <td class="text-end nowrap">
              <span v-if="hasValue(line.debit)">{{ formatAmount(line.debit) }}</span>
              <span v-else class="text-muted">&mdash;</span>
            </td>
            <td class="text-end nowrap">
              <span v-if="hasValue(line.credit)">{{ formatAmount(line.credit) }}</span>
              <span v-else class="text-muted">&mdash;</span>
            </td>
          </tr>
          </tbody>
          <tfoot>
          <tr class="total-row">
            <th colspan="4" class="text-end">Total</th>
            <th class="text-end nowrap">{{ formatAmount(preview.debit_total) }}</th>
            <th class="text-end nowrap">{{ formatAmount(preview.credit_total) }}</th>
          </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div v-if="hasPreview && isClassTab" class="px-2 mt-1 pb-2">
      <div class="table-responsive">
        <table class="table table-bordered table-hover mb-0">
          <thead>
          <tr>
            <th>Asset Class</th>
            <th>Cost Centre</th>
            <th>Business Unit</th>
            <th>Project</th>
            <th>Depreciation Expense (Dr)</th>
            <th>Accumulated Depreciation (Cr)</th>
            <th class="text-end nowrap">Amount</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in preview.classes" :key="index">
            <td class="fw-bold">{{ item.product_name }}</td>
            <td>{{ dimension(item.cost_centre_name) }}</td>
            <td>{{ dimension(item.business_name) }}</td>
            <td>{{ dimension(item.project_name) }}</td>
            <td>{{ dimension(item.expense_account_head_name) }}</td>
            <td>{{ dimension(item.accumulated_account_head_name) }}</td>
            <td class="text-end nowrap">{{ formatAmount(item.amount) }}</td>
          </tr>
          </tbody>
          <tfoot>
          <tr class="total-row">
            <th colspan="6" class="text-end">Total</th>
            <th class="text-end nowrap">{{ formatAmount(preview.amount) }}</th>
          </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import {computed, inject, onMounted, ref} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import handleAssetRegister from '@/services/modules/assetRegister/assetRegister'

const $route = useRoute()
const $router = useRouter()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const {fetchDepreciationJournalPreview, storeDepreciationJournal} = handleAssetRegister()

const loading = ref(false)
const preview = ref(null)
const errorMessage = ref('')
const invalidClasses = ref([])
const activeTab = ref('ledger')
const formData = ref({
  date: '',
  note: ''
})

const hasPreview = computed(() => preview.value !== null)
const hasInvalidClasses = computed(() => invalidClasses.value.length > 0)
const isLedgerTab = computed(() => activeTab.value === 'ledger')
const isClassTab = computed(() => activeTab.value === 'class')

const isPosted = computed(() => {
  if (!preview.value) return false
  return preview.value.is_posted === true
})

const canCreate = computed(() => {
  if (!hasPreview.value) return false
  return !isPosted.value
})

const pageTitle = computed(() => {
  if (isPosted.value) return 'Depreciation Journal Voucher'
  return 'Create Depreciation Journal'
})

const periodLabel = computed(() => {
  if (!preview.value) return ''
  return `${preview.value.format_start_date} to ${preview.value.format_end_date}`
})

const lineCount = computed(() => {
  if (!preview.value) return 0
  return preview.value.lines.length
})

const classCount = computed(() => {
  if (!preview.value) return 0
  return preview.value.classes.length
})

const isBalanced = computed(() => {
  if (!preview.value) return false
  return Number(preview.value.debit_total) === Number(preview.value.credit_total)
})

const hasValue = (amount) => {
  if (amount === null || amount === undefined) return false
  return Number(amount) !== 0
}

const dimension = (value) => {
  if (!value) return '-'
  return value
}

const formatAmount = (amount) => {
  if (amount === null || amount === undefined) return '0.00'
  return Number(amount).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})
}

const getQuery = () => {
  return `?company_id=${$route.params.companyId}`
}

const getPreview = () => {
  loading.value = true
  fetchDepreciationJournalPreview($route.params.processTrackerId, getQuery())
      .then(res => {
        if (!res.status) {
          errorMessage.value = res.message
          setInvalidClasses(res.data)
          return
        }
        preview.value = res.data
        formData.value.date = res.data.end_date
      })
      .finally(() => loading.value = false)
}

const setInvalidClasses = (data) => {
  if (!data) return
  if (!data.invalid_classes) return
  invalidClasses.value = data.invalid_classes
}

const submitJournal = () => {
  if (!formData.value.date) {
    showError('Please select the voucher date')
    return
  }
  if (!isBalanced.value) {
    showError('Debit and credit totals do not match')
    return
  }
  loading.value = true
  storeDepreciationJournal($route.params.processTrackerId, formData.value, getQuery())
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        showSuccess(res.message)
        goBack()
      })
      .finally(() => loading.value = false)
}

const goBack = () => {
  $router.push({
    name: 'depreciation-journal',
    params: {
      companyId: $route.params.companyId,
      moduleId: $route.params.moduleId,
      menuId: $route.params.menuId,
      pageId: $route.params.pageId
    }
  })
}

onMounted(() => {
  getPreview()
})
</script>

<style scoped>
.summary-tile {
  border: 1px solid #e4e6eb;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  background: #fbfcfd;
}

.summary-tile-accent {
  background: #dbe7f2;
  border-color: #c5d7e8;
}

.summary-tile-danger {
  background: #fceaea;
  border-color: #f3c2c2;
}

.summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6e6b7b;
  margin-bottom: 0.25rem;
}

.summary-value {
  font-size: 1rem;
  font-weight: 600;
  color: #4b4b4b;
}

.summary-amount {
  font-size: 1.35rem;
}

.posted-bar {
  border: 1px solid #e4e6eb;
  border-left: 3px solid #28c76f;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  background: #fbfcfd;
}

.posted-item {
  font-size: 0.875rem;
  color: #4b4b4b;
}

.posted-label {
  color: #6e6b7b;
  margin-right: 0.25rem;
}

.nowrap {
  white-space: nowrap;
}

.total-row th {
  background-color: #f2f2f2;
}

table thead th {
  white-space: nowrap;
}
</style>
